import {
  actionColumn,
  ButtonMenu,
  column,
  GridColumn,
  GridDataRow,
  GridTable,
  IconButton,
  ScrollableContent,
  selectColumn,
  simpleDataRows,
  SimpleHeaderAndData,
  useComputed,
  useGridTableApi,
  useSnackbar,
} from "@homebound/beam";
import { useCallback, useMemo, useState } from "react";
import { SearchBox, tagCell } from "src/components";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { FeatureFlagType } from "src/generated/graphqlTypes";
import {
  useActivateUsersMutation,
  useDeactivateUsersMutation,
  UsersPageQuery,
  useStartInternalUserImpersonationMutation,
  useUsersPageQuery,
} from "src/routes/admin/users/UsersPage.generated";
import { PageHeader } from "src/routes/layout/PageHeader";
import { TableActions } from "src/routes/layout/TableActions";
import { queryResult } from "src/utils";

type Row = SimpleHeaderAndData<UsersPageQuery["internalUsers"][number]>;
type InternalUserRow = UsersPageQuery["internalUsers"][number];

/** Lists all internal users and allows deactivating/activating. */
export function UsersPage() {
  const [searchFilter, setSearchFilter] = useState<string>("");
  const query = useUsersPageQuery();
  const [activateUsers] = useActivateUsersMutation({ refetchQueries: ["UsersPage"] });
  const [deactivateUsers] = useDeactivateUsersMutation({ refetchQueries: ["UsersPage"] });
  const [startImpersonation] = useStartInternalUserImpersonationMutation();
  const { triggerNotice } = useSnackbar();
  const tableApi = useGridTableApi<Row>();
  const isReadonly = !useFeatureFlag(FeatureFlagType.BlueprintAdmin);

  const handleStartImpersonation = useCallback(
    async (internalUser: InternalUserRow) => {
      if (!internalUser.user?.isActive) {
        triggerNotice({ icon: "error", message: "Cannot impersonate an inactive user" });
        return;
      }
      await startImpersonation({ variables: { input: { impersonateInternalUserId: internalUser.id } } });
      // Session effective user is resolved on the server from the same JWT; reload to pick up impersonation.
      window.location.reload();
    },
    [startImpersonation, triggerNotice],
  );

  const columns = useMemo(
    () => createColumns(handleStartImpersonation, isReadonly),
    [handleStartImpersonation, isReadonly],
  );

  const selectedRows = useComputed(() => tableApi.getSelectedRows("data"), [tableApi]);

  return queryResult(query, {
    data: ({ internalUsers }) => {
      return (
        <>
          <PageHeader title="Users" />
          <TableActions onlyRight>
            <SearchBox onSearch={setSearchFilter} />
            {!isReadonly && (
              <ButtonMenu
                items={[
                  {
                    label: `Activate Selected Users (${selectedRows.length})`,
                    disabled: selectedRows.length === 0,
                    onClick: async () => {
                      await activateUsers({ variables: { input: { id: selectedRows.map((r) => r.data.user.id!) } } });
                      tableApi.clearSelections();
                    },
                  },
                  {
                    label: `Deactivate Selected Users (${selectedRows.length})`,
                    disabled: selectedRows.length === 0,
                    onClick: async () => {
                      await deactivateUsers({ variables: { input: { id: selectedRows.map((r) => r.data.user.id!) } } });
                      tableApi.clearSelections();
                    },
                  },
                ]}
                trigger={{ label: "Actions" }}
              />
            )}
          </TableActions>
          <ScrollableContent>
            <GridTable
              key={isReadonly ? "readonly" : "editable"}
              api={isReadonly ? undefined : tableApi}
              style={{ rowHeight: "fixed" }}
              columns={columns}
              rows={createRows(internalUsers)}
              filter={searchFilter}
              stickyHeader
            />
          </ScrollableContent>
        </>
      );
    },
  });
}

const nameColumn = column<Row>({ id: "name", header: "Name", data: (d) => d.name });
const emailColumn = column<Row>({ id: "email", header: "Email", data: (d) => d.email });
const statusColumn = column<Row>({
  id: "status",
  header: "Status",
  data: (d) => tagCell(d.user?.isActive ? "success" : "neutral", d.user?.isActive ? "Active" : "Inactive"),
  w: "108px",
});

function createColumns(onImpersonate: (internalUser: InternalUserRow) => void, isReadonly: boolean): GridColumn<Row>[] {
  return [
    ...(isReadonly ? [] : [selectColumn<Row>({ w: "32px" })]),
    nameColumn,
    emailColumn,
    statusColumn,
    ...(isReadonly
      ? []
      : [
          actionColumn<Row>({
            id: "actions",
            header: "Actions",
            data: (internalUser) =>
              internalUser.user?.isActive ? (
                <IconButton
                  data-testid="impersonateButton"
                  icon="impersonate"
                  label="Impersonate this user"
                  onClick={() => onImpersonate(internalUser)}
                />
              ) : null,
            w: "80px",
          }),
        ]),
  ].compact();
}

function createRows(internalUsers: UsersPageQuery["internalUsers"]): GridDataRow<Row>[] {
  return simpleDataRows(internalUsers);
}
